import { useCallback } from "react";
import {
  Button,
  HeadingText,
  ParagraphText,
  StackItem,
  StackLayout,
  queryHelpers,
  styled,
  useMediaQuery,
} from "@leagueplatform/genesis-core";
import {
  EVENT_NAME,
  trackAnalyticsEvent,
  usePageViewAnalytics,
} from "@leagueplatform/analytics";
import { useIntl } from "@leagueplatform/locales";
import {
  ANALYTICS_SCREEN_NAMES,
  DETAIL,
  PRODUCT_AREA_NAMES,
  SECONDARY_CONTENT_MAX_WIDTH,
  SUB_PRODUCT_AREA_NAMES,
} from "common/constants";
import { OnboardingFlowContainer } from "components/onboarding-flow-container/onboarding-flow-container.component";
import JourneyDisclaimerImage from "assets/journey-disclaimer.svg";

const FullScreenContainer = styled(StackLayout, {
  width: "100vw",
  height: "100dvh",
  position: "fixed",
  top: "0",
  left: "0",
  zIndex: "$sticky",
  overflowY: "auto",
});

type Props = {
  onContinue: () => void;
};

// Informational screen shown after onboarding feature highlights and
// before the All Set screen.
export const JourneyDisclaimerPage = ({ onContinue }: Props) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down("tablet"));

  usePageViewAnalytics({
    product_area: PRODUCT_AREA_NAMES.FOUNDATION,
    sub_product_area: SUB_PRODUCT_AREA_NAMES.ONBOARDING,
    screen_name: ANALYTICS_SCREEN_NAMES.HEALTH_JOURNEY_DISCLAIMER,
  });

  const handleContinue = useCallback(() => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA_NAMES.FOUNDATION,
      sub_product_area: SUB_PRODUCT_AREA_NAMES.ONBOARDING,
      screen_name: ANALYTICS_SCREEN_NAMES.HEALTH_JOURNEY_DISCLAIMER,
      detail: DETAIL.CONTINUE,
    });
    onContinue();
  }, [onContinue]);

  return (
    <FullScreenContainer
      css={{
        background: "$surfaceBackgroundPrimary",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <OnboardingFlowContainer
        isMobile={isMobile}
        css={{
          maxWidth: SECONDARY_CONTENT_MAX_WIDTH,
          width: "100%",
          margin: "auto",
        }}
      >
        <StackLayout
          horizontalAlignment="center"
          verticalAlignment="center"
          css={{ maxWidth: "468px", margin: "auto" }}
        >
          <img
            src={JourneyDisclaimerImage}
            height="176"
            width="176"
            alt={formatMessage({ id: "STR_JOURNEY_DISCLAIMER_TITLE" })}
          />
          <StackItem
            horizontalAlignment="center"
            css={{
              marginBlock: "$twoAndHalf",
              textAlign: "center",
              gap: "$half",
            }}
          >
            <HeadingText level="3" size="lg">
              {formatMessage({ id: "STR_JOURNEY_DISCLAIMER_TITLE" })}
            </HeadingText>
            <ParagraphText
              css={{
                typography: "$bodyOne",
                color: "$onSurfaceTextSubdued",
              }}
            >
              {formatMessage({ id: "STR_JOURNEY_DISCLAIMER_BODY" })}
            </ParagraphText>
          </StackItem>
          <StackItem css={{ gap: "$oneAndHalf", width: "100%" }}>
            <Button width="fillContainer" onClick={handleContinue}>
              {formatMessage({ id: "STR_CONTINUE" })}
            </Button>
          </StackItem>
        </StackLayout>
      </OnboardingFlowContainer>
    </FullScreenContainer>
  );
};
